import React, { useState, useRef, useEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from '../../theme/icons.js';
import { popupPlacement } from './popupPlacement.js';
import { passt } from '../../features/suche.js';
import './Select.css';

/**
 * Accessible M3 dropdown. Drop-in replacement for native <select>.
 *
 * Unlike a native <select>, the trigger ellipsizes long labels (with a title
 * tooltip) and the open popup shows the full text (wrapping if needed) — so
 * long event/group names are never lost.
 *
 * Props:
 *   value       current value (compared as string)
 *   onChange    (value) => void
 *   options     [{ value, label }]
 *   placeholder text shown when nothing is selected
 *   ariaLabel   accessible label for the trigger
 *   className   extra class on the root wrapper
 *   searchable  if true, show a search input to filter options
 *   pinnedValue option value that should always appear first (never filtered)
 */
export function Select({
  value,
  onChange,
  options = [],
  placeholder = 'Auswählen…',
  ariaLabel,
  className = '',
  searchable = false,
  pinnedValue,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState('');
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const baseId = useId();

  const selectedIndex = options.findIndex((o) => String(o.value) === String(value));
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  // Filtered options: pinned item always first, then matching options
  const filteredOptions = useCallback(() => {
    let list = options;
    const hasPinned = pinnedValue !== undefined && pinnedValue !== null;
    // If pinned, always put pinned item first
    if (hasPinned) {
      const pinned = list.find((o) => String(o.value) === String(pinnedValue));
      if (pinned) {
        list = [pinned, ...list.filter((o) => String(o.value) !== String(pinnedValue))];
      }
    }
    // If searching, filter remaining (after pinned)
    if (searchable && searchQuery) {
      // Akzente egal: „diozesan" findet „Diözesanlager" (features/suche.js).
      const pinned = hasPinned ? list[0] : null;
      const rest = list.slice(1).filter((o) => passt(o.label, searchQuery));
      return pinned ? [pinned, ...rest] : rest;
    }
    return list;
  }, [options, searchable, searchQuery, pinnedValue]);

  // Close when clicking outside (trigger + portal popup).
  const popupRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    function onDocPointer(e) {
      const insideRoot = rootRef.current && rootRef.current.contains(e.target);
      const insidePopup = popupRef.current && popupRef.current.contains(e.target);
      if (!insideRoot && !insidePopup) setOpen(false);
    }
    document.addEventListener('mousedown', onDocPointer);
    return () => document.removeEventListener('mousedown', onDocPointer);
  }, [open]);

  // On open: highlight the selected option. Fokus: siehe flaecheDa unten.
  useEffect(() => {
    if (!open) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // When search query changes, reset activeIndex so it stays in bounds of filtered list.
  useEffect(() => {
    if (!open || !searchable || !searchQuery) return;
    const opts = filteredOptions();
    if (activeIndex >= opts.length) setActiveIndex(0);
  }, [searchQuery, open, searchable]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the highlighted option in view.
  useEffect(() => {
    if (open && listRef.current && activeIndex >= 0) {
      listRef.current.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [open, activeIndex]);

  const choose = useCallback(
    (opt) => {
      if (!opt) return;
      onChange?.(opt.value);
      setSearchQuery('');
      setOpen(false);
      triggerRef.current?.focus();
    },
    [onChange]
  );

  function onTriggerKeyDown(e) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      setOpen(true);
    }
  }

  function onSearchKeyDown(e) {
    const opts = filteredOptions();
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(opts.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        choose(opts[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  }

  function onListKeyDown(e) {
    const opts = filteredOptions();
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(opts.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => {
          if (i > 0) return i - 1;
          // Move focus back to search input
          searchRef.current?.focus();
          return 0;
        });
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(opts.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        choose(opts[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  }

  // Portal: render popup into document.body to escape parent stacking contexts.
  const portalRef = useRef(null);
  const [popupRect, setPopupRect] = useState(null);

  useEffect(() => {
    if (!open || !triggerRef.current) return;

    function position() {
      /* Die Fläche liegt auf position:fixed, rechnet also in
         Fensterkoordinaten — kein scrollX/scrollY dazu und wieder ab. */
      setPopupRect(popupPlacement(triggerRef.current.getBoundingClientRect(), window.innerHeight));
    }

    position();
    window.addEventListener('scroll', position, true);
    window.addEventListener('resize', position);
    return () => {
      window.removeEventListener('scroll', position, true);
      window.removeEventListener('resize', position);
    };
  }, [open]);

  /* Fokus erst, wenn die Fläche wirklich da ist. Sie hängt an popupRect,
     und das setzt erst der Effekt oben — beim ersten Öffnen gab es das
     Suchfeld im Öffnen-Effekt also noch gar nicht, der Fokus blieb auf
     <body>, und wer gleich lostippte, tippte ins Leere. preventScroll, weil
     die Fläche fest positioniert ist: Die Seite soll nicht springen. */
  const flaecheDa = open && popupRect !== null;
  useEffect(() => {
    if (!flaecheDa) return;
    (searchable ? searchRef : listRef).current?.focus({ preventScroll: true });
  }, [flaecheDa]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build the popup JSX once so we can render it via portal
  const popupContent = open ? (
    <div
      className={`m3-select-popup${popupRect?.flip ? ' is-above' : ''}`}
      style={popupRect ? { maxHeight: popupRect.maxHeight } : undefined}
    >
      {searchable && (
        <div className="m3-select-search">
          <Search size={16} className="m3-select-search-icon" aria-hidden="true" />
          <input
            ref={searchRef}
            type="text"
            className="m3-select-search-input"
            placeholder="Suchen…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
            aria-label="Optionen filtern"
          />
        </div>
      )}
      <ul
        role="listbox"
        tabIndex={-1}
        ref={listRef}
        aria-label={ariaLabel}
        aria-activedescendant={activeIndex >= 0 ? `${baseId}-opt-${activeIndex}` : undefined}
        onKeyDown={onListKeyDown}
      >
        {filteredOptions().map((opt, i) => {
          const isSelected = String(opt.value) === String(value);
          return (
            <li
              id={`${baseId}-opt-${i}`}
              key={opt.value}
              role="option"
              aria-selected={isSelected}
              className={`m3-select-option ${i === activeIndex ? 'active' : ''} ${
                isSelected ? 'selected' : ''
              }`}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => choose(opt)}
            >
              <span className="m3-select-option-label">{opt.label}</span>
              {isSelected && <Check size={16} className="m3-select-check" aria-hidden="true" />}
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  return (
    <div className={`m3-select-root ${className}`} ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="m3-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={selected?.label || ''}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="m3-select-value">{selected ? selected.label : placeholder}</span>
        <ChevronDown size={18} className="m3-select-chevron" aria-hidden="true" />
      </button>

      {/* Portal container — invisible, used as anchor */}
      <div
        ref={portalRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          overflow: 'visible',
          pointerEvents: 'none',
        }}
      />

      {/* Render popup via portal to escape stacking contexts */}
      {popupRect &&
        createPortal(
          <div
            ref={popupRef}
            style={{
              position: 'fixed',
              top: popupRect.top,
              bottom: popupRect.bottom,
              left: popupRect.left,
              width: popupRect.width,
              zIndex: 9999,
            }}
          >
            {popupContent}
          </div>,
          document.body
        )}
    </div>
  );
}

export default Select;
